import React, { useState, useEffect } from 'react';
import { BellRing, Plus, X, Trash2, ToggleLeft, ToggleRight, MapPin, Tag, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserSubscriptions,
  createJobSubscription,
  deleteJobSubscription,
  toggleSubscription,
  type JobSubscription,
} from '../../lib/supabase/jobSubscriptions';

export default function JobAlertSettings() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<JobSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [keywordInput, setKeywordInput] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [location, setLocation] = useState('');

  useEffect(() => {
    if (user) loadSubscriptions();
  }, [user]);

  const loadSubscriptions = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const subs = await getUserSubscriptions(user.id);
      setSubscriptions(subs);
    } catch {
      setError('Failed to load your alert subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const word = keywordInput.trim().toLowerCase();
      if (word && !keywords.includes(word)) {
        setKeywords([...keywords, word]);
      }
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const handleCreateAlert = async () => {
    if (!user) return;
    if (keywords.length === 0) {
      setError('Add at least one keyword');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const newSub = await createJobSubscription(user.id, keywords, location || 'Any');
      setSubscriptions([newSub, ...subscriptions]);
      setKeywords([]);
      setLocation('');
      setSuccess('Job alert created! You will be notified when matching jobs are posted.');
      setTimeout(() => setSuccess(null), 4000);
    } catch {
      setError('Failed to create alert');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (sub: JobSubscription) => {
    try {
      const updated = await toggleSubscription(sub.id, !sub.is_active);
      setSubscriptions(subscriptions.map((s) => (s.id === sub.id ? updated : s)));
    } catch {
      setError('Failed to update alert');
    }
  };

  const handleDelete = async (subId: string) => {
    try {
      await deleteJobSubscription(subId);
      setSubscriptions(subscriptions.filter((s) => s.id !== subId));
    } catch {
      setError('Failed to delete alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-teal-50 to-green-50 border border-teal-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <BellRing className="h-5 w-5 text-teal-600" />
          <h3 className="font-semibold text-teal-900">How Alerts Work</h3>
        </div>
        <p className="text-sm text-teal-800 leading-relaxed">
          Choose keywords that match the type of jobs you are looking for. When a new job is posted that matches your keywords,
          you will receive a notification instantly.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          {success}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-gray-900">Create New Alert</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <Tag className="h-3.5 w-3.5 inline mr-1" />
            Keywords
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {keywords.map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center gap-1 bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full text-sm font-medium"
              >
                {kw}
                <button onClick={() => handleRemoveKeyword(kw)} className="hover:text-teal-950">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleAddKeyword}
            placeholder="Type a keyword and press Enter (e.g. ranch hand, fencing, cattle)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <MapPin className="h-3.5 w-3.5 inline mr-1" />
            Location (optional)
          </label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Any location"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <button
          onClick={handleCreateAlert}
          disabled={saving || keywords.length === 0}
          className="inline-flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create Alert
        </button>
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">
          Your Alerts {subscriptions.length > 0 && <span className="text-gray-500 font-normal text-sm">({subscriptions.length})</span>}
        </h3>

        {subscriptions.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <BellRing className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No alerts set up yet. Create one above to get started.</p>
          </div>
        ) : (
          subscriptions.map((sub) => (
            <div
              key={sub.id}
              className={`border rounded-xl p-4 transition-colors ${
                sub.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {sub.keywords.map((kw) => (
                      <span
                        key={kw}
                        className="inline-block bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full text-xs font-medium"
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {sub.location || 'Any'}
                    </span>
                    <span>
                      Created {new Date(sub.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggle(sub)}
                    className="text-gray-500 hover:text-teal-600 transition-colors"
                    title={sub.is_active ? 'Pause alert' : 'Resume alert'}
                  >
                    {sub.is_active ? (
                      <ToggleRight className="h-6 w-6 text-teal-600" />
                    ) : (
                      <ToggleLeft className="h-6 w-6" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                    title="Delete alert"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
